import { Component, OnInit}           from '@angular/core';
import { ActivatedRoute, Params }     from '@angular/router';
import { Router }                     from '@angular/router';

import { JourneysService }            from './../journeys.service';
import { EditionService }             from './../../../shared/services/edition.service';
import { AllPermissions }             from '../../../models/permissions';
import { PermissionsService }         from '../../../shared/services/permissions.service';

import { JourneyDetail }              from './../../../models/journey/journey-detail';
import { JourneyUser }                from './../../../models/journey/journey-user';
import { ExistingUser }               from '../../../models/program/existing-user';
import { CsvService }                 from '../../program-list/csv.service';
import { ErrorService }               from '../../../shared/services/error.service';
import { FormBuilder, FormGroup }     from '@angular/forms';

@Component({
  selector: 'app-journey',
  templateUrl: './journey.component.html',
  styleUrls: ['./journey.component.less'],
  providers: [JourneysService]
})

export class JourneyComponent implements OnInit {
  canEditJourney: boolean;
  journey: JourneyDetail;
  public showUsersAdd: boolean;
  public saving: boolean;
  toDelete: JourneyUser[];
  currentUsers: JourneyUser[];
  tabNames: {analytics?: string, users?: string, content?: string}
  journeyId: number;
  savingMetadata: boolean;
  showDeleteUser: boolean;
  showDeleteJourney: boolean;
  syncToggleLabel: string = "Publish Journey to all learners";
  public errors: string[];
  public minimalStateObj: {categoryType: string, mapping: string, categoryId?: number};
  public journeyLink: string;
  public subscriptions: {id: number, name: string}[] = [];
  public subscriptionSettings: FormGroup;
  public savingSubscriptionSettings: boolean;
  public showChangeSubscription: boolean;
  private permissions: AllPermissions;

  constructor(
    private journeysService: JourneysService,
    private editionService: EditionService,
    private csvService: CsvService,
    private errorService: ErrorService,
    private permissionsService: PermissionsService,
    private route: ActivatedRoute,
    private router: Router,
    private _fb: FormBuilder
  ) { }

  ngOnInit(): void {
    this.permissionsService.retrievePermissions().subscribe(permissions => {
      this.permissions = permissions;

      if (this.permissions.listSubscriptions) {
        this.csvService.retrieveSubscriptionAndPlanOptions().subscribe(options => {
          this.subscriptions = options.subscription_options;
        });
      } else {
        this.subscriptions = [];
      }
    });
    this.canEditJourney = this.permissions.editJourneys;
    this.tabNames = {
      users: "Users",
    }
    if (this.permissions.analyticsAccess) {
      this.tabNames['analytics'] = "Go To Analytics";
    }
    if (this.canEditJourney) {
      this.tabNames['content'] = "Content";
    }
    this.toDelete = [];
    this.errors = [];
    this.showDeleteUser = false;
    this.route.params.forEach((params: Params) => {
      this.journeyId = +params['journeyId'];
      this.minimalStateObj = {categoryType: 'journey', mapping: 'journey', categoryId: this.journeyId};
      this.getJourney();
    });

    this.editionService.getEdition().subscribe(edition => {
      const base = edition.base_url;
      this.journeyLink = `${base}/programs?journey=${this.journeyId}`;
    });

    this.subscriptionSettings = this._fb.group({
      subscription_id: [null]
    });
  }

  private getJourney(reload?: boolean) {
    return new Promise<void>((resolve) => {
      this.journeysService
        .getJourney(this.journeyId, reload)
        .subscribe(journey => {
          this.journey = journey;
          this.currentUsers = journey.users;
          if (this.journey.subscription_name) {
            this.syncToggleLabel = `Publish Journey to all ${this.journey.subscription_name} learners`;
          }
          this.subscriptionSettings.patchValue({
            subscription_id: this.journey.private_for
          });
          this.subscriptionSettings.markAsPristine();
          resolve();
        });
    });
  }

  public deleteUsers(): void {
    let users = this.toDelete;
    let ids = users.map(u => u.id);
    this.journeysService.deleteUsers(this.journeyId, ids)
    .subscribe(
      result => {
        this.currentUsers = this.currentUsers.filter(u => result.indexOf(u.id) < 0);
      },
      error => {
        this.errors.push("An error occured while attempting to remove Journey users.")
      }
    );
    this.toDelete = [];
    this.showDeleteUser = false;
  }

  public deleteJourney(): void {
    console.log('deleteJourney!')
    this.journeysService.deleteJourney(this.journeyId)
    .subscribe(
      result => {
        this.routeToJourneys();
      },
      error => {
        this.errors.push("An error occured while attempting to delete the Journey.")
      }
    );
  }

  public getNameList(users: JourneyUser[]): string {
    let names = users.map(u => u.name);
    if (names.length > 1) {
      names[names.length - 1] = "and " + names[names.length - 1];
    }
    return names.length == 2 ? names.join(' ') : names.join(', ');
  }

  public confirmDeletion(users: JourneyUser[]): void {
    this.toDelete = users;
    this.showDeleteUser = true;
  }

  public updateUserInDelete(user: JourneyUser): void {
    if(this.toDelete.indexOf(user) >= 0){
      this.toDelete = this.toDelete.filter((u) => u !== user);
    } else {
      this.toDelete.push(user);
    }
  }

  public selectAllUsers(event): void {
    event.preventDefault();
    if (this.toDelete && this.toDelete.length) {
      this.toDelete = [];
    }
    else {
      this.toDelete = this.currentUsers;
    }

  }

  public menuItemSelected(item: string): void {
    let path = `/programs?journey=${this.journeyId}`;
    this.editionService.jumpToLearning(path);
  }

  public userInToDelete(user: JourneyUser): boolean {
    return this.toDelete.findIndex(u => u.id == user.id) > -1;
  }

  public handleContentUpdate(journey: JourneyDetail): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.savingMetadata = true;
      this.journeysService
      .submitJourneyEdits(journey)
      .subscribe(() => {
        this.journey = journey;
        if (this.journey.subscription_name) {
          this.syncToggleLabel = `Publish Journey to all ${this.journey.subscription_name} learners`;
        }
        this.savingMetadata = false;
        resolve();
      });
    });
  }

  private routeToJourneys(): void {
    this.router.navigate(["dashboard", "journeys"]);
  }

  public addUsers(users: ExistingUser[]): void {
    let ids = users.map(u => u.id);
    this.saving = true;
    this.journeysService.addUsers(this.journeyId, ids)
    .subscribe(
      result => {
        users.forEach((u) => {
          let journeyUser = {
            id: u.id,
            name: u.name,
            email: u.email,
            added: result.added,
          } as JourneyUser
          this.currentUsers.push(journeyUser)
        });
        this.getJourney();
        this.showUsersAdd = !this.showUsersAdd;
        this.errors=[];
        this.saving = false;
      },
      error => {
        this.errors.push("An error occured while attempting to add Journey users.")
        this.saving = false;
      }
    );
  }

  public handleTabClick(title: string): void {
    if (title == this.tabNames.analytics) {
      this.router.navigate(["analytics", "journeys"], {
        queryParams: {
          journey: this.journeyId
        }
      });
    }
  }

  public addUsersByCsv(file: any): void {
    this.errors = [];
    const postFile = file;
    const formData = new FormData();
    formData.append('upload', postFile);
    this.saving = true;
    this.csvService.postCsvJourneyUsers(this.journeyId, formData)
      .subscribe(
        result => {
          result['added'].forEach((u) => {
            let journeyUser = {
              id: u.id,
              name: u.name,
              email: u.email,
              added: result.added,
            } as JourneyUser
            this.currentUsers.push(journeyUser)
          });
          if (result['missing'].length) {
            this.errors.push(`The following ${result['field']}s did not match any existing users: ${result['missing'].join(', ')}`);
          }
          this.getJourney();
          this.showUsersAdd = !this.showUsersAdd;
          this.saving = false;
      }, error => {
        this.errors = this.errors.concat(this.errorService.messages(error));
        this.saving = false;
      });
  }

  public async setSyncSubscriptionUsers(value: boolean): Promise<void> {
    let updatedJourney = JSON.parse(JSON.stringify(this.journey));
    updatedJourney.sync_subscription_users = value;
    await this.handleContentUpdate(updatedJourney);
    if(value) {
      this.getJourney(true);
    }
  }

  public async setEnableWelcomeNotification(value: boolean): Promise<void> {
    let updatedJourney = JSON.parse(JSON.stringify(this.journey));
    updatedJourney.enable_welcome_notification = value;
    await this.handleContentUpdate(updatedJourney);
  }

  public async saveSubscriptionSettings(event): Promise<void> {
    event.preventDefault();
    if (this.savingMetadata || this.savingSubscriptionSettings) {
      return;
    }
    this.savingSubscriptionSettings = true;
    let updatedJourney = JSON.parse(JSON.stringify(this.journey));
    updatedJourney.private_for = this.subscriptionSettings.controls['subscription_id'].value;
    await this.handleContentUpdate(updatedJourney);
    await this.getJourney(true);
    this.savingSubscriptionSettings = false;
    this.showChangeSubscription = false;
  }
}



// WEBPACK FOOTER //
// /home/runner/work/nomadic-admin/nomadic-admin/nomadic-admin/src/app/dashboard/journeys/journey/journey.component.ts